import { useState, useCallback } from 'react';
import { uploadFilesWithSignedUrl, FileUploadType } from '../services/utilityService';
import toast from 'react-hot-toast';

// File upload state interface
interface FileUploadState {
  uploading: boolean;
  progress: number;
  uploadedFiles: UploadedFile[];
  errors: string[];
}

// Uploaded file interface
interface UploadedFile {
  fileName: string;
  fileUrl: string;
  filePath: string;
  fieldName?: string;
}

// Upload configuration
interface UploadConfig {
  recordId: string;
  type: FileUploadType;
  fieldName?: string;
  maxSize?: number; // in MB
  allowedTypes?: string[];
  onProgress?: (progress: number) => void;
  onSuccess?: (files: UploadedFile[]) => void;
  onError?: (error: string) => void;
}

/**
 * Custom hook for handling file uploads with signed URLs
 * Reusable across all components that need file upload functionality
 */
export const useFileUpload = (config: UploadConfig) => {
  const [uploadState, setUploadState] = useState<FileUploadState>({
    uploading: false,
    progress: 0,
    uploadedFiles: [],
    errors: []
  });

  // Validate file before upload
  const validateFile = useCallback((file: File): string | null => {
    const { maxSize = 50, allowedTypes } = config;
    
    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSize) {
      return `File size exceeds ${maxSize}MB limit`;
    }

    // Check file type if specified
    if (allowedTypes && allowedTypes.length > 0) {
      const fileType = file.type.toLowerCase();
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      
      const isValidType = allowedTypes.some(type => 
        fileType.includes(type.toLowerCase()) || 
        fileExt === type.toLowerCase()
      );
      
      if (!isValidType) {
        return `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`;
      }
    }

    return null;
  }, [config]);

  // Upload single file
  const uploadFile = useCallback(async (file: File): Promise<UploadedFile | null> => {
    const { recordId, type, fieldName, onProgress, onError } = config;

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      onError?.(validationError);
      toast.error(validationError);
      return null;
    }

    setUploadState(prev => ({
      ...prev,
      uploading: true,
      progress: 0,
      errors: []
    }));

    try {
      const result = await uploadFilesWithSignedUrl(
        [file],
        recordId,
        type,
        {
          onProgress: (progressData) => {
            setUploadState(prev => ({
              ...prev,
              progress: progressData.progress
            }));
            onProgress?.(progressData.progress);
          }
        }
      );

      if (result.successful && result.successful.length > 0) {
        const uploadedFile: UploadedFile = {
          fileName: file.name,
          fileUrl: result.successful[0].fileUrl,
          filePath: result.successful[0].filePath,
          fieldName: fieldName
        };

        setUploadState(prev => ({
          ...prev,
          uploading: false,
          progress: 100,
          uploadedFiles: [...prev.uploadedFiles, uploadedFile]
        }));

        toast.success(`Successfully uploaded ${file.name}`);
        config.onSuccess?.([uploadedFile]);

        return uploadedFile;
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      
      setUploadState(prev => ({
        ...prev,
        uploading: false,
        progress: 0,
        errors: [...prev.errors, errorMessage]
      }));

      onError?.(errorMessage);
      toast.error(`Failed to upload ${file.name}: ${errorMessage}`);
      
      return null;
    }
  }, [config, validateFile]);

  // Upload multiple files
  const uploadFiles = useCallback(async (files: File[]): Promise<UploadedFile[]> => {
    const { recordId, type, fieldName, onProgress, onSuccess, onError } = config;

    // Validate all files
    const validationErrors: string[] = [];
    const validFiles: File[] = [];

    files.forEach(file => {
      const error = validateFile(file);
      if (error) {
        validationErrors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    if (validationErrors.length > 0) {
      const errorMessage = validationErrors.join(', ');
      onError?.(errorMessage);
      toast.error(`Validation errors: ${errorMessage}`);
      return [];
    }

    if (validFiles.length === 0) {
      return [];
    }

    setUploadState(prev => ({
      ...prev,
      uploading: true,
      progress: 0,
      errors: []
    }));

    try {
      const result = await uploadFilesWithSignedUrl(
        validFiles,
        recordId,
        type,
        {
          onProgress: (progressData) => {
            setUploadState(prev => ({
              ...prev,
              progress: progressData.progress
            }));
            onProgress?.(progressData.progress);
          }
        }
      );

      const successfulUploads: UploadedFile[] = [];
      const uploadErrors: string[] = [];

      if (result && result.successful) {
        result.successful.forEach((file: any) => {
          successfulUploads.push({
            fileName: file.fileName,
            fileUrl: file.fileUrl,
            filePath: file.filePath || file.fileUrl,
            fieldName: fieldName
          });
        });
      }

      if (result && result.failed) {
        result.failed.forEach((file: any) => {
          uploadErrors.push(`${file.fileName}: ${file.error || 'Upload failed'}`);
        });
      }

      setUploadState(prev => ({
        ...prev,
        uploading: false,
        progress: 100,
        uploadedFiles: [...prev.uploadedFiles, ...successfulUploads],
        errors: uploadErrors
      }));

      if (successfulUploads.length > 0) {
        toast.success(`Successfully uploaded ${successfulUploads.length} file(s)`);
        onSuccess?.(successfulUploads);
      }

      if (uploadErrors.length > 0) {
        const errorMessage = uploadErrors.join(', ');
        onError?.(errorMessage);
        toast.error(`Some uploads failed: ${errorMessage}`);
      }

      return successfulUploads;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Batch upload failed';
      
      setUploadState(prev => ({
        ...prev,
        uploading: false,
        progress: 0,
        errors: [errorMessage]
      }));

      onError?.(errorMessage);
      toast.error(errorMessage);
      
      return [];
    }
  }, [config, validateFile]);

  // Reset upload state
  const resetUpload = useCallback(() => {
    setUploadState({
      uploading: false,
      progress: 0,
      uploadedFiles: [],
      errors: []
    });
  }, []);

  // Clear specific uploaded file
  const removeUploadedFile = useCallback((fileName: string) => {
    setUploadState(prev => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter(f => f.fileName !== fileName)
    }));
  }, []);

  return {
    // State
    ...uploadState,
    
    // Actions
    uploadFile,
    uploadFiles,
    resetUpload,
    removeUploadedFile,
    
    // Utils
    validateFile
  };
};

export default useFileUpload;